// Ref: https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/theme/src/components/alert.ts
// The Toast component uses Alert under the hood, we have to override Alert's styles

import { transparentize } from '@/util/chakra';
import { alertAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    cssVar,
    StyleFunctionProps,
} from '@chakra-ui/styled-system';

const { definePartsStyle, defineMultiStyleConfig } =
    createMultiStyleConfigHelpers(parts.keys);

const $fg = cssVar('alert-fg');
const $bg = cssVar('alert-bg');

const baseStyle = definePartsStyle({
    container: {
        bg: $bg.reference,
        px: '4',
        pt: '3',
        pb: '3.5',
    },
    title: {
        fontSize: '14',
        fontWeight: '700',
        lineHeight: '6',
        marginEnd: '2',
    },
    description: {
        fontSize: '14',
        lineHeight: '5',
        marginRight: '2',
    },
    icon: {
        color: $fg.reference,
        flexShrink: 0,
        mt: '0.5',
        marginEnd: '3',
        w: '4',
        h: '4',
    },
    spinner: {
        color: $fg.reference,
        flexShrink: 0,
        marginEnd: '3',
        w: '5',
        h: '5',
    },
});

function getBg(props: StyleFunctionProps) {
    const { theme, colorScheme: c } = props;
    const darkBg = transparentize(`${c}.200`, 0.16)(theme);
    return {
        light: `colors.${c}.50`,
        dark: darkBg,
    };
}

const variantSubtle = definePartsStyle((props) => {
    const { colorScheme: c } = props;
    const bg = getBg(props);
    return {
        container: {
            [$fg.variable]: `colors.${c}.600`,
            [$bg.variable]: bg.light,
            _dark: {
                [$fg.variable]: `colors.${c}.200`,
                [$bg.variable]: bg.dark,
            },
        },
    };
});

const variantLeftAccent = definePartsStyle((props) => {
    const { colorScheme: c } = props;
    const bg = getBg(props);
    return {
        container: {
            [$fg.variable]: `colors.${c}.600`,
            [$bg.variable]: bg.light,
            _dark: {
                [$fg.variable]: `colors.${c}.200`,
                [$bg.variable]: bg.dark,
            },
            paddingStart: '3',
            borderStartWidth: '4px',
            borderStartColor: $fg.reference,
        },
    };
});

const variantTopAccent = definePartsStyle((props) => {
    const { colorScheme: c } = props;
    const bg = getBg(props);
    return {
        container: {
            [$fg.variable]: `colors.${c}.600`,
            [$bg.variable]: bg.light,
            _dark: {
                [$fg.variable]: `colors.${c}.200`,
                [$bg.variable]: bg.dark,
            },
            pt: '2',
            borderTopWidth: '4px',
            borderTopColor: $fg.reference,
        },
    };
});

const variantSolid = definePartsStyle((props) => {
    const { colorScheme: c } = props;
    return {
        container: {
            [$fg.variable]: `colors.white`,
            [$bg.variable]: `colors.${c}.600`,
            _dark: {
                [$fg.variable]: `colors.gray.900`,
                [$bg.variable]: `colors.${c}.200`,
            },
            color: $fg.reference,
        },
    };
});

const variants = {
    subtle: variantSubtle,
    'left-accent': variantLeftAccent,
    'top-accent': variantTopAccent,
    solid: variantSolid,
};

const alertTheme = defineMultiStyleConfig({
    baseStyle,
    variants,
    defaultProps: {
        variant: 'subtle',
        colorScheme: 'blue',
    },
});

export default alertTheme;
